<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { TableFoldProps as BookedAirRoadTableFoldProps } from '@/components/tableFolds/BookedAirRoadTableFold.vue';
import type { HeadersData } from '@/composables/useTable';
import type { DialogName } from '@/stores/useDialogStore';

import { computed, reactive, ref, toRaw } from 'vue';
import _cloneDeep from 'lodash/cloneDeep';

import KBtn from '@/components/btns/KBtn.vue';
import TableFoldBtn from '@/components/btns/TableFoldBtn.vue';
import KCheckboxField from '@/components/fields/KCheckboxField.vue';
import TableLoader from '@/components/loaders/TableLoader.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import BookedAirRoadTableFold from '@/components/tableFolds/BookedAirRoadTableFold.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import JobIdAndIconLinkUnit from '@/components/units/JobIdAndIconLinkUnit.vue';
import useTable from '@/composables/useTable';
import { useDialogStore } from '@/stores/useDialogStore';
import { fetchPostAirroadJobsDownloadLabelsFile } from '@/helpers/fetchers';

import tableStyles from '@/styles/table.module.scss';

export interface DataItem {
  id: number;
  jobId: number;
  jobDate: string;
  customerCode: string;
  status: string;
  connote: string;
  reference1: string;
  reference2: string;
  labels: boolean;
  fold: BookedAirRoadTableFoldProps;
}

export interface Props {
  loading?: boolean;
  data?: DataItem[];
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
});

const model = defineModel<Record<string, boolean>>({
  default: () => ({}),
});

const dialogStore = useDialogStore();

const printDialogData = reactive({
  arJobId: NaN,
  arJobConnote: '',
});

function openPrintDialog() {
  const dialogName: DialogName = 'print';
  dialogStore.open(dialogName, _cloneDeep(toRaw(printDialogData)));
}

function handlePrintClick(arJobId: number, arJobConnote: string): void {
  printDialogData.arJobId = arJobId;
  printDialogData.arJobConnote = arJobConnote;
  openPrintDialog();
}

const openFoldKey = ref(NaN);

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Ref<DataItem[]>;

const headersData = computed<HeadersData>(function () {
  // if (props.isStaff) { // use computed to keep the pattern from other tables
  return [
    {
      value: 'jobId',
      title: 'Job ID',
      colspan: 2,
    },
    {
      value: 'jobDate',
      title: 'Date',
    },
    {
      value: 'customerCode',
      title: 'Customer',
    },
    {
      value: 'status',
      title: 'Status',
    },
    {
      value: 'connote',
      title: 'Connote',
    },
    {
      value: 'reference1',
      title: 'Reference 1',
    },
    {
      value: 'reference2',
      title: 'Reference 2',
    },
    {
      value: 'actions',
      title: 'Actions',
      isNotSortable: true,
    },
  ];
});

const foldColspan = computed(() => {
  return headersData.value.length + 1;
});

function handleClick(dataItemId: number): void {
  if (openFoldKey.value === dataItemId) {
    openFoldKey.value = NaN;
  } else {
    openFoldKey.value = dataItemId;
  }
}

const loadings = ref<Record<string, boolean>>({});

async function handleDownloadLabelClick(
  dataItem: ReadonlyDeep<DataItem>,
): Promise<void> {
  if (loadings.value[dataItem.id]) return;
  loadings.value[dataItem.id] = true;
  await fetchPostAirroadJobsDownloadLabelsFile(dataItem.connote, [
    dataItem.id,
  ]).finally(() => {
    loadings.value[dataItem.id] = false;
  });
}
</script>

<template>
  <table
    :class="[
      tableStyles.table,
      tableStyles.folded,
      tableStyles.hovered,
      tableStyles.bordered,
      $style.bookedAirRoadTable,
    ]"
    data-testid="bookedAirRoadTable"
  >
    <thead>
      <tr>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText />
      </td>
    </tr>
    <tbody v-else>
      <template v-for="dataItem in sortedData" :key="dataItem.id">
        <tr
          :class="[
            tableStyles.foldTitle,
            { [tableStyles.foldOpen]: openFoldKey === dataItem.id },
          ]"
        >
          <td :class="$style.checkboxFoldTd">
            <div :class="$style.checkboxGroup">
              <KCheckboxField
                v-model="model[dataItem.id]"
                hide-details
                :disabled="!dataItem.labels"
                size="sm"
              />
            </div>

            <TableFoldBtn
              :class="tableStyles.foldBtn"
              :is-open="openFoldKey === dataItem.id"
              @click="handleClick(dataItem.id)"
            />
          </td>
          <td :class="$style.jobIdTd">
            <JobIdAndIconLinkUnit
              :job-id="dataItem.jobId"
              :job-date="dataItem.jobDate"
            />
          </td>
          <td :class="$style.jobDateTd">
            {{ dataItem.jobDate }}
          </td>
          <td :class="$style.customerCodeTd">
            {{ dataItem.customerCode }}
          </td>
          <td :class="$style.statusTd">
            {{ dataItem.status }}
          </td>
          <td :class="$style.connoteTd">
            {{ dataItem.connote }}
          </td>
          <td :class="$style.reference1Td">
            {{ dataItem.reference1 }}
          </td>
          <td :class="$style.reference2Td">
            {{ dataItem.reference2 }}
          </td>
          <td :class="$style.actionsTd">
            <menu :class="$style.actionsMenuList">
              <li :class="$style.actionsMenuListItem">
                <KBtn
                  variant="text"
                  icon="mdi-printer-outline"
                  :disabled="!dataItem.labels"
                  @click="handlePrintClick(dataItem.id, dataItem.connote)"
                ></KBtn>
              </li>
              <li :class="$style.actionsMenuListItem">
                <KParentTooltip location="top">Download label</KParentTooltip>
                <KBtn
                  variant="text"
                  icon="mdi-download-outline"
                  :loading="loadings[dataItem.id]"
                  :disabled="!dataItem.labels"
                  @click="handleDownloadLabelClick(dataItem)"
                ></KBtn>
              </li>
            </menu>
          </td>
        </tr>
        <tr
          :class="[
            tableStyles.foldContent,
            { [tableStyles.foldOpen]: openFoldKey === dataItem.id },
          ]"
        >
          <td :colspan="foldColspan">
            <div v-if="openFoldKey === dataItem.id">
              <BookedAirRoadTableFold v-bind="dataItem.fold" />
            </div>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.bookedAirRoadTable {
  width: 100%;

  & > thead > tr {
    & > th {
      white-space: nowrap;
    }
    & > th {
      position: sticky;
      top: variables.$appBarHeight;
      z-index: 1;
    }
  }
}
  .jobIdTh {
  }
  .jobDateTh {
    min-width: 100px;
    width: 100px;
  }
  .customerCodeTh {
  }
  .stop1Th {
  }
  .stop2Th {
  }
  .stop3Th {
  }
  .reference1Th {
  }
  .reference2Th {
  }
  .reference3Th {
  }
  .checkboxFoldTd {
    min-width: 67px !important;
    width: 67px !important;
  }
  .jobIdTd {
    min-width: 90px;
    width: 90px;
  }
  .jobDateTd {
    white-space: nowrap;
  }
  .customerCodeTd {
  }
  .statusTd {
  }
  .connoteTd {
  }
  .reference1Td {
  }
  .reference2Td {
  }
  .actionsTd {
    white-space: nowrap;
    text-align: center;
  }
    .actionsMenuList {
      list-style: none;
      padding: 0;
      margin: -12px -10px;
    }
      .actionsMenuListItem {
        display: inline;
      }

    .checkboxGroup {
      position: absolute;
      top: calc(50% - 36px / 2);
      right: calc(67px - 32.5px);
    }
</style>
